/**
 * ContentCard Component
 */
import React, { ReactElement, forwardRef } from 'react';
import { ContentCardProps, ContentCardOrientations, ContentCardSkeletonProps } from './contentCard.types';
import {
  ContentCardWrapper,
  ContentWrapper,
  TitleWrapper,
  ImageWrapper,
  DataWrapper,
  Metadata,
  TimePill,
  Icon,
  Image,
} from './contentCard.style';
import { ImageProps } from '../Image/image.types';
import { IconSizes, IconProps } from '../Icon/icon.types';
import Skeleton from '../Skeleton/skeleton';

import COLORS from '../../style/universal.color';
import wrapLink from '../../utils/linkWrapperHelper';
import Handler from '../../commonTypes/handler.types';
import { LinkProps, LinkRef } from '../Link/link.types';
import FixAspect from '../utilComponents/FixedAspect';

// Wrapping this in a function, so we can update here and remove magic values from the component.
const getSkeletonProps = (imageProps: ImageProps): ContentCardSkeletonProps & { isAnimated?: boolean } => ({
  headlineCount: 2,
  headlineWidth: '100%',
  headlineMargin: 5,
  headlineHeight: '15px',
  dateWidth: '30%',
  dateHeight: '10px',
  dateMargin: 5,
  imageBorderRadius: imageProps.borderRadius ? imageProps.borderRadius.replace(/[^0-9]/g, '') : 0,
  imageWidth: imageProps.width || '100%',
  imageHeight: imageProps.height || '100%',
});

const { LANDSCAPE } = ContentCardOrientations;
const ContentCard = forwardRef(
  (
    {
      imageProps,
      headline,
      publishDate,
      orientation = LANDSCAPE,
      handlers = {},
      videoProps,
      isLoading,
      className,
      dataAttr,
      url,
      target,
      rel,
      isActive,
      isActiveText,
      optionsComponent,
      ariaLabel,
    }: ContentCardProps,
    ref?: LinkRef,
  ) => {
    const defaultIcon: IconProps = {
      name: 'play-overlay',
      size: IconSizes.LG,
      fill: COLORS.mlbGrey7,
    };
    const defaultIsPlayingText: string = 'NOW PLAYING';

    // Call the handler when the event is triggered
    const invokeHandler = (type: string, e?: Event) => {
      handlers[type](e);
    };

    let handlerProps: Handler | undefined;
    Object.keys(handlers).forEach((key: string) => {
      handlerProps = handlerProps || {};
      handlerProps[key] = (e: Event) => {
        invokeHandler(key, e);
      };
    });

    let linkProps: LinkProps | undefined;
    let completeContentCardProps: object = {
      'aria-label': ariaLabel || headline,
      orientation,
      isLoading,
      ref,
    };
    // If we have a URL passed in, construct a LinkProps object to pass down.
    // If not, pass in additional props to the contentCard wrapper instead.
    if (url) {
      linkProps = {
        url,
        className,
        rel,
        target,
        dataAttr: { ...dataAttr },
        handlers: { ...handlerProps },
        ...(ref && { ref }),
      };
    } else {
      completeContentCardProps = Object.assign(completeContentCardProps, {
        className,
        ...dataAttr,
        ...handlerProps,
      });
    }

    // Setting defaults if these are not passed in.
    const iconProps = videoProps && videoProps.iconProps ? videoProps.iconProps : defaultIcon;
    const hideIcon =
      videoProps && Object.prototype.hasOwnProperty.call(videoProps, 'hideIcon') ? videoProps.hideIcon : false;

    // If we have a URL passed in, then we need to wrap

    const {
      imageWidth,
      imageHeight,
      imageBorderRadius,
      headlineWidth,
      headlineHeight,
      headlineMargin,
      headlineCount,
      dateWidth,
      dateHeight,
      dateMargin,
      isAnimated,
    } = getSkeletonProps(imageProps);

    const imageSkeleton = (
      <Skeleton width={imageWidth} height={imageHeight} borderRadius={imageBorderRadius} animation={isAnimated} />
    );
    const content: ReactElement = (
      <ContentCardWrapper {...completeContentCardProps}>
        <ImageWrapper
          isLoading={isLoading}
          isActive={isActive}
          isActiveText={isActiveText || defaultIsPlayingText}
          imageProps={imageProps}
          orientation={orientation}
          fixAspect={imageProps.fixAspect}
        >
          {isLoading ? (
            // this is the skeleton for the image, so we use the same imageProps dimensions
            imageProps && imageProps.fixAspect ? (
              <FixAspect ratio={imageProps && imageProps.ratio}>{imageSkeleton}</FixAspect>
            ) : (
              imageSkeleton
            )
          ) : (
            <>
              <Image
                {...imageProps}
                width={imageProps && imageProps.width ? imageProps.width : '100%'}
                height={imageProps && imageProps.height ? imageProps.height : '100%'}
              />
              {videoProps && !hideIcon && (
                <Icon name={iconProps.name} size={iconProps.size} fill={iconProps.fill} isActive={isActive} />
              )}
              {videoProps && <TimePill isActive={isActive}>{videoProps.duration}</TimePill>}
            </>
          )}
        </ImageWrapper>
        <ContentWrapper orientation={orientation}>
          {isLoading ? (
            <>
              <Skeleton
                width={headlineWidth}
                height={headlineHeight}
                margin={headlineMargin}
                count={headlineCount}
                animation={isAnimated}
              />
              <Skeleton width={dateWidth} height={dateHeight} margin={dateMargin} animation={isAnimated} />
            </>
          ) : (
            <DataWrapper>
              <Metadata>
                <TitleWrapper videoProps={videoProps}>
                  {videoProps && videoProps.callOutTextProps && videoProps.callOutTextProps.text && (
                    <span> {videoProps.callOutTextProps.text} </span>
                  )}
                  {headline}
                </TitleWrapper>
                <p>{publishDate}</p>
              </Metadata>
              {optionsComponent}
            </DataWrapper>
          )}
        </ContentWrapper>
      </ContentCardWrapper>
    );
    return wrapLink(content, linkProps);
  },
);

export default ContentCard;
